import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { AuthScreen, SubmitButton } from '@/components/AuthScreen';
import { Field } from '@/components/Field';
import { useRegister } from '@/hooks/useSession';
import { useT } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { detectTimeZone } from '@/lib/dates';

export function RegisterPage() {
  const navigate = useNavigate();
  const t = useT();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [consent, setConsent] = useState(false);

  const signUp = useRegister();
  const failure = signUp.error instanceof ApiError ? signUp.error : null;
  const fieldErrors = failure?.fieldErrors ?? {};

  const update = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();

    signUp.mutate(
      { ...form, biometric_consent: consent },
      {
        // Straight to the screen they will need next — the account exists but
        // cannot be signed into until the link is opened, and this is where
        // they come back to afterwards.
        //
        // The address rides in router state rather than the query string: it
        // is personal data, and a URL is written to nginx's access log and the
        // browser's history. It is worth carrying at all because seeing it
        // spelled out is the one chance anyone gets to notice a typo, which is
        // the likeliest reason the mail never turns up. `replace` so Back does
        // not return to a filled-in form for an account already created.
        onSuccess: () =>
          void navigate('/login', { replace: true, state: { registered: form.email } }),
      },
    );
  };

  return (
    <AuthScreen
      title={t('auth.register_title')}
      subtitle={t('auth.register_subtitle', { zone: detectTimeZone() })}
      error={failure && failure.status !== 422 ? failure.message : null}
      footer={
        <Link to="/login" className="underline">
          {t('auth.have_account')}
        </Link>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field
          label={t('auth.name')}
          name="name"
          autoComplete="name"
          required
          value={form.name}
          error={fieldErrors.name}
          onChange={update('name')}
        />

        <Field
          label={t('auth.email')}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          error={fieldErrors.email}
          onChange={update('email')}
        />

        <Field
          label={t('auth.password')}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={form.password}
          error={fieldErrors.password}
          onChange={update('password')}
        />

        <Field
          label={t('auth.confirm_password')}
          type="password"
          name="password_confirmation"
          autoComplete="new-password"
          required
          value={form.password_confirmation}
          error={fieldErrors.password_confirmation}
          onChange={update('password_confirmation')}
        />

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            name="biometric_consent"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
            className="mt-1"
          />
          {/* Photographs of faces are biometric data, so consent is a separate,
              explicit act rather than something implied by signing up. */}
          <span>
            {t('auth.consent')}
            {fieldErrors.biometric_consent && (
              <span className="block text-red-500">{fieldErrors.biometric_consent}</span>
            )}
          </span>
        </label>

        <SubmitButton pending={signUp.isPending}>
          {signUp.isPending ? t('auth.creating_account') : t('auth.create_account')}
        </SubmitButton>
      </form>
    </AuthScreen>
  );
}
